import { useRef, useEffect } from 'react';

import ButtonWithBubble from '@components/Button/ButtonWithBubble';

import { formatCurrency, formatNumber, getCurrencySymbol } from '../formatters';
import styles from '../style.module.scss';
import { type CalculationResult, type CurrencyType } from '../types';

interface CalculatorResultsProps {
  result: CalculationResult;
  currency: CurrencyType;
  totalEmployees: number;
  turnoverRate: number;
  resultLabel: string;
  guideUrl: string;
  guideImageUrl: string;
  guideImageAlt: string;
  guideBackImageUrl: string;
  guideCardHeading: string;
  guideCtaText: string;
  scrollKey: number;
  onGuideClick?: () => void;
}

export function CalculatorResults({
  result,
  currency,
  totalEmployees,
  turnoverRate: _turnoverRate,
  resultLabel,
  guideUrl,
  guideImageUrl,
  guideImageAlt,
  guideBackImageUrl,
  guideCardHeading,
  guideCtaText,
  scrollKey,
  onGuideClick,
}: CalculatorResultsProps) {
  const resultsRef = useRef<HTMLDivElement>(null);
  const symbol = getCurrencySymbol(currency);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (resultsRef.current) {
        const nav = document.querySelector('.site-header, header');
        const navHeight = nav ? nav.getBoundingClientRect().height : 0;
        const offset = navHeight + 55;
        const top = resultsRef.current.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
      resultsRef.current?.focus({ preventScroll: true });
    }, 200);
    return () => clearTimeout(timer);
  }, [scrollKey]);

  return (
    <div className={styles.resultsSection} ref={resultsRef} tabIndex={-1} aria-live="polite">
      <div className={styles.resultsRow}>
        {/* Left: data & table */}
        <div className={styles.resultsData}>
          <p className={styles.resultsEyebrow}>{resultLabel}</p>
          <p className={styles.resultsAmount}>
            {formatCurrency(result.avoidableSaving, currency, true)}{' '}
            <span className={styles.resultsAmountSuffix}>per year</span>
          </p>
          <p className={styles.resultsDescription}>
            Your organization loses {formatNumber(result.totalDepartures)} people each year, costing
            approximately {formatCurrency(result.totalCost, currency)}. With{' '}
            {formatNumber(totalEmployees)} employees, reducing turnover by just{' '}
            {result.improvementPoints}% would mean {formatNumber(result.avoidableDepartures)} fewer
            departures, saving around {formatCurrency(result.avoidableSaving, currency)} every year.
          </p>

          <p className={styles.resultsResearchNote}>
            Every figure below is derived from real-world workforce research.
          </p>

          {/* Breakdown table */}
          <div className={styles.resultsTableWrap}>
            <div className={styles.resultsTable}>
              <div className={styles.resultsTableHeader}>
                <span className={styles.colRole}>Role level</span>
                <span className={styles.colCenter}>Departures</span>
                <span className={styles.colCenter}>Salary</span>
                <span className={styles.colCenter}>Replacement cost</span>
                <span className={styles.colRight}>Annual total</span>
              </div>

              {result.tiers.map((tier) => (
                <div className={styles.resultsTableRow} key={tier.label}>
                  <span className={styles.colRole}>{tier.label}</span>
                  <span className={styles.colCenter}>{formatNumber(tier.departures)}</span>
                  <span className={styles.colCenter}>
                    {symbol}
                    {formatNumber(tier.salary)}
                  </span>
                  <span className={styles.colCenter}>
                    {symbol}
                    {formatNumber(tier.replacementCost)} ({tier.replacementPercent}%)
                  </span>
                  <span className={`${styles.colRight} ${styles.colOrange}`}>
                    {formatCurrency(tier.annualTotal, currency)}
                  </span>
                </div>
              ))}

              <div className={styles.resultsTableTotal}>
                <span className={styles.colRole}>
                  <strong>Total</strong>
                </span>
                <span className={styles.colCenter}>
                  <strong>{formatNumber(result.totalDepartures)}</strong>
                </span>
                <span />
                <span />
                <span className={`${styles.colRight} ${styles.colOrange}`}>
                  <strong>{formatCurrency(result.totalCost, currency)}</strong>
                </span>
              </div>
            </div>
          </div>

          <p className={styles.resultsImprovementNote}>
            Reducing your voluntary turnover by just {result.improvementPoints}% results in{' '}
            {formatNumber(result.avoidableDepartures)} fewer departures, saving approximately{' '}
            <strong className={styles.colOrange}>
              {formatCurrency(result.avoidableSaving, currency)} per year.
            </strong>
          </p>
        </div>

        {/* Right: guide CTA card */}
        <div className={styles.guideCard}>
          <div className={styles.guideCardContent}>
            {guideCardHeading && <p className={styles.guideCardHeading}>{guideCardHeading}</p>}
            {guideCtaText && (
              <div
                className={styles.guideCardText}
                dangerouslySetInnerHTML={{ __html: guideCtaText }}
              />
            )}
            <ButtonWithBubble
              url={guideUrl}
              variant="primary"
              className={styles.calcButton}
              onClick={onGuideClick}
            >
              Download the full guide
            </ButtonWithBubble>
          </div>
          {(guideImageUrl || guideBackImageUrl) && (
            <div className={styles.guideCardBooks}>
              {guideBackImageUrl && (
                <div className={styles.guideCardBookSide}>
                  <img src={guideBackImageUrl} alt="" />
                </div>
              )}
              {guideImageUrl && (
                <div className={styles.guideCardBookCenter}>
                  <img src={guideImageUrl} alt={guideImageAlt || ''} />
                </div>
              )}
              {guideBackImageUrl && (
                <div className={styles.guideCardBookSide}>
                  <img src={guideBackImageUrl} alt="" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
